// src/pages/theatre/AddStaff.jsx
import React, { useState, useEffect } from 'react';
import PageHeading from '../../components/PageHeading';
import {
  TeamOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import {
  Button,
  Input,
  message,
  Table,
  Modal,
  Form,
  Select,
  Switch,
  Popconfirm,
  Tag,
} from 'antd';
import axiosInstance from '../../utils/axios';

const AddStaff = () => {
  const [staff, setStaff] = useState([]);
  const [theatres, setTheatres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [modalVisible, setModalVisible] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTheatres();
  }, []);

  useEffect(() => {
    fetchStaff(pagination.current, search);
  }, [pagination.current, search]);

  const fetchTheatres = async () => {
    try {
      const response = await axiosInstance.get('theatre-management/select');
      setTheatres(response.data);
    } catch (error) {
      message.error('Failed to load theatres');
    }
  };

  const fetchStaff = async (page = 1, searchTerm = '') => {
    setTableLoading(true);
    try {
      let url = `theatre-management/staff/?page=${page}&page_size=${pagination.pageSize}`;
      if (searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await axiosInstance.get(url);
      setStaff(response.data.results || []);
      setPagination((prev) => ({
        ...prev,
        current: page,
        total: response.data.count || 0,
      }));
    } catch (error) {
      message.error('Failed to fetch staff members');
    } finally {
      setTableLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        email: values.email,
        name: values.name,
        mobile: values.mobile || null,
        theatre_id: values.theatre_id,
        is_active: values.is_active,
      };

      if (!editingStaff || values.password) {
        payload.password = values.password;
      }

      if (editingStaff) {
        await axiosInstance.patch(`theatre-management/staff/${editingStaff.id}/`, payload);
        message.success('Staff updated successfully');
      } else {
        await axiosInstance.post('theatre-management/staff/', payload);
        message.success('Staff added successfully');
      }

      form.resetFields();
      setModalVisible(false);
      setEditingStaff(null);
      fetchStaff(pagination.current, search);
    } catch (error) {
      const errMsg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        JSON.stringify(error.response?.data) ||
        'Operation failed';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (record) => {
    if (!record.theatres || record.theatres.length === 0) {
      message.error('Cannot delete: no theatre assigned');
      return;
    }
    const theatreId = record.theatres[0].id;

    try {
      await axiosInstance.delete(`theatre-management/staff/${theatreId}/${record.id}/`);
      message.success('Staff deleted successfully');
      fetchStaff(pagination.current, search);
    } catch (error) {
      message.error('Failed to delete staff');
    }
  };

  const handleEdit = (record) => {
    setEditingStaff(record);
    const theatreId = record.theatres[0]?.id;

    form.setFieldsValue({
      name: record.name,
      email: record.email,
      mobile: record.mobile || '',
      theatre_id: theatreId,
      is_active: record.is_active,
    });
    setModalVisible(true);
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Theatre',
      key: 'theatre',
      render: (_, record) =>
        record.theatres && record.theatres.length > 0 ? (
          <Tag color="blue">{record.theatres[0].name}</Tag>
        ) : (
          '—'
        ),
    },
    { title: 'Mobile', dataIndex: 'mobile', key: 'mobile', render: (text) => text || '—' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'status',
      render: (is_active) => (
        <span style={{ color: is_active ? '#52c41a' : '#f5222d' }}>
          {is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Action',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Delete this staff member?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div className="dashboard">
      <PageHeading name="Staff Management" icon={<TeamOutlined />} />

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Search by name or email"
          prefix={<SearchOutlined />}
          style={{ width: '240px' }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
        />

        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => {
            setEditingStaff(null);
            form.resetFields();
            setModalVisible(true);
          }}
        >
          Add Staff
        </Button>
      </div>

      <Table
        dataSource={staff}
        columns={columns}
        rowKey="id"
        loading={tableLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (page) => {
            setPagination((prev) => ({ ...prev, current: page }));
            fetchStaff(page, search);
          },
        }}
      />

      {/* Add / Edit Modal */}
      <Modal
        title={editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingStaff(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* Theatre Field - Disabled on Edit */}
          <Form.Item
            name="theatre_id"
            label="Assign Theatre"
            rules={[{ required: true, message: 'Please select one theatre' }]}
            normalize={(value) => (Array.isArray(value) ? value[0] : value)}
          >
            <Select
              placeholder="Select a theatre"
              disabled={!!editingStaff}
            >
              {theatres.map((th) => (
                <Select.Option key={th.id} value={th.id}>
                  {th.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Show current theatre name when editing */}
          {editingStaff && editingStaff.theatres && editingStaff.theatres.length > 0 && (
            <div style={{ marginBottom: 16, color: '#888', fontSize: 13 }}>
              Currently assigned to: <strong>{editingStaff.theatres[0].name}</strong>
            </div>
          )}

          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter full name' }]}
          >
            <Input placeholder="Enter full name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Invalid email format' },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="mobile"
            label="Mobile Number"
            rules={[
              { required: true, message: 'Please enter mobile number' },
              { pattern: /^[0-9]{10}$/, message: 'Enter a valid 10-digit number' },
            ]}
          >
            <Input placeholder="e.g. 9876543210" maxLength={10} />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              {
                required: !editingStaff,
                message: 'Please enter password',
              },
            ]}
          >
            <Input.Password
              placeholder={
                editingStaff
                  ? 'Leave blank to keep current password'
                  : 'Set a strong password'
              }
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Button
              onClick={() => {
                setModalVisible(false);
                setEditingStaff(null);
                form.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingStaff ? 'Update' : 'Add'} Staff
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AddStaff;